/**
 * @fileoverview What to plant: the gardener's own plants (any stage, the Seed vault included) that can go in the ground now, worked out by the
 * backend (bloom-backend services/plants/whatToPlant, D67) and sent ready to show: the groups and their order, the rows'
 * sentences (the very line the plant's card will show), the tag, and the notice for Free, a paused plan or a place with no dates. The app draws it and
 * works out none of it.
 *
 * E19 (a gardener with few or no plants): beside the list the server sends at once "From your to-do list", the
 * "Want something that's not here?" card and the Add sheet's words (addWords); a moment later a second answer
 * (getWhatToPlantCatalogue) brings the See them card and the picker (the
 * Add sheet's words), so the list never waits for the whole catalogue (owner, 29 Oct). E24: the search is its own
 * answer (getWhatToPlantSearch): the app sends what she typed and the server sends the rows to draw, in order and in
 * groups (types by name, "Romaine Lettuce", with Add filing that type; hers first, her plants marked "In your garden"
 * with "Open it", then her to-do crops marked "From your to-do list"), with the closing line filled in. The See them page has
 * its own answer (getWhatToPlantBrowse), and Add is one call (addCropFromWhatToPlant). The app fills {plant} into the
 * Add sheet's words.
 *
 * Where: features/what-to-plant (useWhatToPlant, useWhatToPlantBrowse, useAddCrop, the pages).
 * API: GET /plants/what-to-plant?lang=, GET /plants/what-to-plant/catalogue?lang=, GET /plants/what-to-plant/browse?lang=,
 * GET /plants/what-to-plant/search?q=&lang=, POST /plants/what-to-plant/add (the
 * garden being tended, set by apiClient's X-Grow-Garden; a member is answered with the owner's place, plan and plants).
 * A failure reaches the app as a 500, reported by the server.
 *
 * @module api/whatToPlant
 */

import { apiClient } from './apiClient';

/**
 * The groups the server sends, in display order; an empty group is absent. missed (B126): plants started indoors whose
 * plant-out days ended while still at Seeding, at the bottom, each row's line saying so.
 */
export type WhatToPlantGroupKey = 'lastWeek' | 'open' | 'soon' | 'missed';

/** One of the gardener's plants that can go in, as the server says it. */
export interface WhatToPlantRow {
  /** The crop (plant_groups id). */
  cropId: number;
  /** The gardener's plant this row is (plant id), whatever its stage, the Seed vault included. */
  plantId: number;
  /** The crop's name in the gardener's language. */
  name: string;
  /** The planting line the plant's card will show once added ("Plant out now through early November"). */
  line: string;
  /** The server's tag ("In your garden" or "In your Seed vault"), else null. */
  tag: string | null;
}

/** One group of rows under its heading. */
export interface WhatToPlantGroup {
  /** Which group. */
  key: WhatToPlantGroupKey;
  /** Its heading, said by the server. */
  heading: string;
  /** The last-week group only: the heading is drawn in the accent ink. */
  urgent?: boolean;
  /** How many rows are drawn before "Show N more" (the row count itself when hiding the rest would hide one row). */
  shown: number;
  /** Every row of the group. */
  rows: WhatToPlantRow[];
}

/** The card that stands in place of (or beside) the list. */
export interface WhatToPlantNotice {
  /** Why: Free or paused ("locked"), or no planting dates for the place ("noDates"). */
  kind: 'locked' | 'noDates';
  /** The server's sentence. */
  line: string;
  /** The button to offer: to the plans, to resume a paused plan, or none. */
  action: 'subscribe' | 'resume' | null;
}

/** One piece of a line of the note under the list: words, and a link when `href` is set. */
export interface WhatToPlantPiece {
  /** The words, said by the server. */
  text: string;
  /** An app path ("/plants?add=1") or a mailto: address; null for plain words. */
  href: string | null;
}

/** The note under the list: where it comes from and how to add more plants. */
export interface WhatToPlantMore {
  /** Lines, each its pieces in order. */
  lines: WhatToPlantPiece[][];
  /** The app path of the seed-packet photo page (also the href of its piece). */
  packetsPath: string;
  /** The garden's seed order email address, or null before it has one. */
  address: string | null;
}

/** Where a crop stands at the garden's place today, as the server decides it. */
export type CropState = 'now' | 'later' | 'noDates';

/** A crop's kind, as the See them page groups it. */
export type CropKind = 'vegetable' | 'herb' | 'fruit' | 'flower' | 'other';

/** One crop as the to-do rows, the See them page and the search draw it (E19); in the search, also a type's (E24). */
export interface WhatToPlantCropRow {
  /** The row's own name: "c:<cropId>" for a crop, "t:<plantId>" for a type, "p:<plantObjId>" for one of her plants. */
  key: string;
  /** The crop (plant_groups id): what Add adds. */
  cropId: number;
  /** The type Add files, on a type's row (E24); null for a crop's (its "Not sure" type). */
  plantId: number | null;
  /** Its name in the gardener's language. */
  name: string;
  /** Its kind. */
  kind: CropKind;
  /** Can go in now, not now (with dates), or no dates here. */
  state: CropState;
  /** The card's planting line ("Sow now through mid-November", "Next: sow mid to late February"), or its kind. */
  line: string;
  /** "Last week", "Not yet", "No dates yet", or null. */
  pill: string | null;
}

/** The search's groups (E24): hers first ('yours', no heading: each row carries its mark), then where each stands. */
export type SearchGroupKey = 'yours' | CropState;

/** One row of the search (E24): a crop, a type, or one of her plants, as the server decides it. */
export interface WhatToPlantSearchRow extends WhatToPlantCropRow {
  /** Her plant, for "Open it" in place of Add, on an "In your garden" row; else null. */
  plantObjId: number | null;
  /** "In your garden", "In your Seed vault", "From your to-do list", or null. */
  mark: string | null;
}

/** The search's answer (E24): the rows that answer what she typed, as the server decided and ordered them. */
export interface WhatToPlantSearch {
  /** The server's success flag. */
  success: boolean;
  /** The plan's state: open, Free or paused (no rows unless open). */
  access: 'open' | 'free' | 'paused';
  /** What was searched, as the server read it. */
  query: string;
  /** How many rows. */
  count: number;
  /** The groups with rows, in order: hers (no heading), "Can go in now", "Not now where you live", "No planting dates yet". */
  groups: Array<{ key: SearchGroupKey; heading: string | null; sub: string | null; rows: WhatToPlantSearchRow[] }>;
  /** "2 plants match "bok choy". Clear the search to see your list again.", or null with nothing. */
  closing: string | null;
  /** With nothing: "Grow doesn't know a plant called "x" yet." and the way to the Add a plant form; else null. */
  nothing: { text: string; addYourself: WhatToPlantPiece } | null;
}

/** "From your to-do list" (E19): the crops her own open to-dos name. */
export interface WhatToPlantTodos {
  /** "From your to-do list". */
  heading: string;
  /** "You wrote this down. Add it and Grow reminds you when it's time." */
  sub: string;
  /** The crops, in the order of her to-dos. */
  rows: WhatToPlantCropRow[];
}

/** "More you could plant now" (E19): the card that opens the See them page. */
export interface WhatToPlantSeeThem {
  /** The section's heading. */
  heading: string;
  /** Its sub line. */
  sub: string;
  /** How many crops can go in now that she has none of. */
  count: number;
  /** "11 more plants" or "11 plants". */
  title: string;
  /** "Vegetables, herbs, fruit and flowers". */
  line: string;
  /** "See them". */
  link: string;
}

/** "Want something that's not here?" (E19): the card that takes her to the search. */
export interface WhatToPlantFindAny {
  /** Its title. */
  title: string;
  /** Its line. */
  line: string;
  /** "Search". */
  link: string;
}

/** The bed an added plant goes to. */
export interface WhatToPlantBed {
  /** location_id. */
  id: number;
  /** Its name. */
  name: string;
  /** "your only bed" or "your first bed". */
  note: string;
}

/** The Add sheet's words ({plant} filled by the app) and the bed (E19). */
export interface WhatToPlantSheetWords {
  /** "Add {plant}?" */
  heading: string;
  /** For a crop with dates. */
  lineDated: string;
  /** For a crop with none here. */
  lineNoDates: string;
  /** "Add {plant}" */
  add: string;
  /** The bed it goes to, or null when she has none. */
  bed: WhatToPlantBed | null;
}

/** What the Add sheet and an added row draw, on both pages (E19; the search is its own answer since E24). */
export interface WhatToPlantPicker {
  /** The Add sheet's words and the bed. */
  sheet: WhatToPlantSheetWords;
  /** The Added state's words: "Added", "Open it". */
  added: { word: string; open: string };
}

/** The server's answer. */
export interface WhatToPlant {
  /** The server's success flag, passed through. */
  success: boolean;
  /** The garden's own day, "YYYY-MM-DD". */
  date: string;
  /** The language of the words. */
  lang: string;
  /** The plan's state for this list: open, Free or paused. */
  access: 'open' | 'free' | 'paused';
  /** The sub line under the heading ("For Antioch, TN, today"). */
  sub: string;
  /** The groups in display order; empty groups are absent, and the list is empty when locked. */
  groups: WhatToPlantGroup[];
  /** The notice, or null. */
  notice: WhatToPlantNotice | null;
  /** The note under the list; null when the list is locked. */
  more: WhatToPlantMore | null;
  /** E19: "From your to-do list", or null (absent when locked). */
  todos?: WhatToPlantTodos | null;
  /** E19: the "Want something that's not here?" card (absent when locked). */
  findAny?: WhatToPlantFindAny | null;
  /** E19: the Add sheet's words and bed, for a to-do row's Add before the catalogue is in (absent when locked). */
  addWords?: { sheet: WhatToPlantSheetWords; added: { word: string; open: string } } | null;
}

/** What to plant's second answer (E19), a moment after the list: the See them card and the search's catalogue. */
export interface WhatToPlantCatalogue {
  /** The server's success flag. */
  success: boolean;
  /** The garden's own day. */
  date: string;
  /** The language of the words. */
  lang: string;
  /** The plan's state: open, Free or paused. */
  access: 'open' | 'free' | 'paused';
  /** The See them card, or null at a count of zero (null when locked). */
  seeThem: WhatToPlantSeeThem | null;
  /** The search and the Add sheet (null when locked). */
  picker: WhatToPlantPicker | null;
}

/** The See them page's answer (E19); everything but access and notice is absent when locked. */
export interface WhatToPlantBrowse {
  /** The server's success flag, passed through. */
  success: boolean;
  /** The garden's own day. */
  date: string;
  /** The language of the words. */
  lang: string;
  /** The plan's state: open, Free or paused. */
  access: 'open' | 'free' | 'paused';
  /** The locked notice, or null. */
  notice: WhatToPlantNotice | null;
  /** "Can go in now where you live". */
  heading?: string;
  /** "For Matthews, NC, today · 11 plants you don't have yet". */
  sub?: string;
  /** "Tap Add and it joins your Plants list as Planning. Grow reminds you when to sow it." */
  promise?: string;
  /** How many crops can go in now that she has none of. */
  count?: number;
  /** The kinds with rows, in order, A to Z within each. */
  kinds?: Array<{ key: CropKind; heading: string; count: number; rows: WhatToPlantCropRow[] }>;
  /** "Herbs and flowers: none can go in right now at your place. …", or null. */
  emptyKinds?: string | null;
  /** The sentence when nothing can go in now, or null. */
  none?: string | null;
  /** The search and the Add sheet. */
  picker?: WhatToPlantPicker;
}

/** What Add answers (E19). */
export interface WhatToPlantAdded {
  /** The server's success flag. */
  success: boolean;
  /** The new plant (its page is /plants/<plantObjId>). */
  plantObjId: number;
  /** Its name. */
  name: string;
  /** "Spinach is on your Plants list now, in My Garden. Not the right bed? Change it from the plant." */
  line: string;
  /** The See them page's sub line worked out again. */
  sub: string;
}

/**
 * Reads what can be planted now.
 *
 * @param lang - The words' language.
 * @returns The server's answer.
 */
export async function getWhatToPlant(lang: string): Promise<WhatToPlant> {
  const response = await apiClient.get<WhatToPlant>('/plants/what-to-plant', { params: { lang } });
  return response.data;
}

/**
 * Reads What to plant's second answer: the See them card and the search's catalogue (E19).
 *
 * @param lang - The words' language.
 * @returns The server's answer.
 */
export async function getWhatToPlantCatalogue(lang: string): Promise<WhatToPlantCatalogue> {
  const response = await apiClient.get<WhatToPlantCatalogue>('/plants/what-to-plant/catalogue', { params: { lang } });
  return response.data;
}

/**
 * Asks the server which rows answer what she typed (E24).
 *
 * @param query - What she typed.
 * @param lang - The words' language.
 * @returns The server's answer.
 */
export async function getWhatToPlantSearch(query: string, lang: string): Promise<WhatToPlantSearch> {
  const response = await apiClient.get<WhatToPlantSearch>('/plants/what-to-plant/search', { params: { q: query, lang } });
  return response.data;
}

/**
 * Reads the See them page: every crop that can go in now that the garden has none of (E19).
 *
 * @param lang - The words' language.
 * @returns The server's answer.
 */
export async function getWhatToPlantBrowse(lang: string): Promise<WhatToPlantBrowse> {
  const response = await apiClient.get<WhatToPlantBrowse>('/plants/what-to-plant/browse', { params: { lang } });
  return response.data;
}

/**
 * Adds a crop, or one of its types (E24), as a Planning plant in the bed the sheet named (E19).
 *
 * @param cropId - The crop.
 * @param plantId - The type, from a type's row; null for the crop alone.
 * @param locationId - The bed, or null for the server's choice.
 * @param lang - The words' language.
 * @returns The server's answer.
 */
export async function addCropFromWhatToPlant(
  cropId: number, plantId: number | null, locationId: number | null, lang: string,
): Promise<WhatToPlantAdded> {
  const response = await apiClient.post<WhatToPlantAdded>('/plants/what-to-plant/add', { cropId, plantId, locationId }, { params: { lang } });
  return response.data;
}
